import Axios from 'axios'
import appConfig from "./config"
import history from "services/history"
import jwt_decode from "jwt-decode";
import { logoutWhenTokenExp } from "services/logoutTokenExp";
import { handleGatewayStatus } from "./gatewayReload";


let baseUrl = appConfig.apiUrl;

let axios = Axios.create({
  withCredentials: false,
  baseURL: baseUrl,
})

// The server rotates the refresh token on every refresh and blacklists the old one,
// so parallel 401s must share one refresh request.
let refreshInFlight = null;

const refreshTokens = () => {
  if (!refreshInFlight) {
    const usedRefreshToken = localStorage.getItem("refreshToken");
    refreshInFlight = axios({
      url: `${baseUrl}/token/refresh/`,
      method: "POST",
      data: { refresh: usedRefreshToken },
      _isTokenRefresh: true,
    }).then(res => {
      localStorage.setItem("accessToken", res.data.access);
      localStorage.setItem("refreshToken", res.data.refresh);
      logoutWhenTokenExp();
    }).catch(errorRefresh => {
      // Another tab may have rotated the token first and stored the new one.
      const currentRefreshToken = localStorage.getItem("refreshToken");
      if (currentRefreshToken && currentRefreshToken !== usedRefreshToken) {
        return;
      }
      throw errorRefresh;
    }).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Blacklists the refresh token on the server so it cannot be used after logout.
export const revokeRefreshToken = () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    return Promise.resolve();
  }
  return axios({
    url: `${baseUrl}/token/blacklist/`,
    method: "POST",
    data: { refresh: refreshToken },
    _isTokenRefresh: true,
  }).catch(() => {});
};

//request interceptor to add the auth token header to requests
axios.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      config.headers["Authorization"] = `jwt ${accessToken}`;
    }
    // Language
    const language = localStorage.getItem("language", "fi");
    config.headers["Accept-Language"] = language
    
    return config;
  },
  (error) => {
    // Promise.reject(error);
    return Promise.reject(error);
  }
);
//response interceptor to refresh token on receiving token expired error
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  function (error) {
    const originalRequest = error.config;

    // 502/503/504: reload once after a short delay. 500 is an application
    // error and must not restart the page.
    try {
      if (!originalRequest?.skipGatewayReload) {
        handleGatewayStatus(error.response?.status);
      }
    } catch (gatewayError) {
      // A failure here must not skip token refresh or the original rejection.
      console.error("Gateway reload handling failed:", gatewayError);
    }
    
    // Log detailed error information for debugging
    console.error('API Error Details:', {
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      code: error.code,
    });
    
    // Token refresh and logout requests are handled by their callers.
    if (originalRequest?._isTokenRefresh) {
      return Promise.reject(error);
    }
    else {
      let refreshToken = localStorage.getItem("refreshToken");
      if (
        refreshToken &&
        error?.response?.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        return refreshTokens().then(() => {
          console.log("Access and refresh token refreshed!");
          return axios(originalRequest);
        }).catch(errorRefresh => {
          console.log("Token refresh failed with status", errorRefresh?.response?.status);
          // Only a rejected refresh token ends the session; network errors keep it.
          if (errorRefresh?.response?.status === 401) {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            window.location.href = "/"
          }
          return Promise.reject(error);
        });
      }
      else {
        const accessToken = localStorage.getItem("accessToken");
        if ((!refreshToken || refreshToken.length == 0) && accessToken && accessToken.length > 0) {
          console.log("91.No refreshToken, log user out")
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          window.location.href = "/"
        }
      }
    }
    // return error?.response;
    console.log(error.response)
    return Promise.reject(error.response || error);
  }
);

/*
//functions to make api calls
const api = {
  signup: (body) => {
    return axios.post(`${baseUrl}/auth/signup`, body);
  },
  login: (body) => {
    return axios.post(`${baseUrl}/auth/login`, body);
  },
  refreshToken: (body) => {
    return axios.post(`${baseUrl}/auth/refresh_token`, body);
  },
  logout: (body) => {
    return axios.delete(`${baseUrl}/auth/logout`, body);
  },
  getProtected: () => {
    return axios.get(`${baseUrl}/protected_resource`);
  },
};
*/
axios.defaults.xsrfCookieName = 'csrftoken'
axios.defaults.xsrfHeaderName = 'X-CSRFToken'

export default axios;